import type React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Facebook from './Facebook';
import Twitter from './Twitter';
import SEOForBlog from './ForBlog';
// import ogImg from '../../../public/images/og-img.png';

const icon32Path = '/images/icon_32.png';

const getAlternates = (path) => {
  const alternates = [
    { hreflang: 'x-default', href: `https://www.lingualbox.com${path}` },
    { hreflang: 'en', href: `https://www.lingualbox.com${path}` },
    { hreflang: 'ja', href: `https://ja.lingualbox.com${path}` },
    { hreflang: 'ru', href: `https://ru.lingualbox.com${path}` },
    { hreflang: 'pt-br', href: `https://pt.lingualbox.com${path}` },
    { hreflang: 'es', href: `https://es.lingualbox.com${path}` },
    { hreflang: 'zh-tw', href: `https://tw.lingualbox.com${path}` },
  ];
  return alternates.map((link) => (
    <link
      key={`alternates-${link.hreflang}`}
      rel="alternate"
      hrefLang={link.hreflang}
      href={link.href}
    />
  ));
};

const getSiteUrl = (language) => {
  switch (language) {
    case 'en':
      return 'https://www.lingualbox.com';
    case 'ja':
      return 'https://ja.lingualbox.com';
    case 'es':
      return 'https://es.lingualbox.com';
    case 'pt':
      return 'https://pt.lingualbox.com';
    case 'tw':
      return 'https://tw.lingualbox.com';
    case 'ru':
      return 'https://ru.lingualbox.com';
    default:
      return 'https://www.lingualbox.com';
  }
};

const getOgLanguage = (language) => {
  switch (language) {
    case 'en':
      return 'en_US';
    case 'ja':
      return 'ja_JP';
    case 'es':
      return 'es_ES';
    case 'pt':
      return 'pt_BR';
    case 'tw':
      return 'zh_TW';
    case 'ru':
      return 'ru_RU';
    default:
      return 'en_US';
  }
};

interface Props {
  title?: string;
  desc?: string;
  banner?: string;
}

const SEO: React.FC<Props> = (props: Props) => {
  const router = useRouter();
  const { locale, asPath: path } = router;
  const { t } = useTranslation('common');
  const { title = null, desc = null, banner = null } = props;
  // const author = 'LingualBox, Inc.';
  const defaultTitle = t('title.default');
  const defaultDescription = t('description.default');
  const headline = t('headline');
  if (path.startsWith('/blog')) return <SEOForBlog {...props} />;
  const siteUrl = getSiteUrl(locale);
  const ogLanguage = getOgLanguage(locale);
  const defaultBanner = '/public/images/og-img.png';

  const seo = {
    title: title || defaultTitle,
    headline,
    description: desc || defaultDescription,
    image: `${siteUrl}${banner || defaultBanner}`,
    url: `${siteUrl}${path || ''}`,
    icon32Url: `${siteUrl}${icon32Path}`,
  };

  // const schemaOrgWebPage = {
  //   '@context': 'http://schema.org',
  //   '@type': 'WebPage',
  //   url: siteUrl,
  //   headline,
  //   inLanguage: language,
  //   mainEntityOfPage: siteUrl,
  //   description: defaultDescription,
  //   name: defaultTitle,
  //   author: {
  //     '@type': 'Organization',
  //     name: author,
  //   },
  //   copyrightHolder: {
  //     '@type': 'Organization',
  //     name: author,
  //   },
  //   copyrightYear: '2020',
  //   creator: {
  //     '@type': 'Organization',
  //     name: author,
  //   },
  //   publisher: {
  //     '@type': 'Organization',
  //     name: author,
  //   },
  //   image: {
  //     '@type': 'ImageObject',
  //     url: seo.image,
  //   },
  //   datePublished: '2012-08-06T16:30:00+08:00',
  //   dateModified: '2020-10-13T16:30:00+08:00'
  // }

  return (
    <>
      <Head>
        <meta
          key="meta-viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, shrink-to-fit=no"
        />
        <meta
          key="meta-compatible"
          httpEquiv="X-UA-Compatible"
          content="IE=edge"
        />
        <meta
          key="content-type-text"
          httpEquiv="Content-Type"
          content="text/html; charset=utf-8"
        />
        <title>{seo.title}</title>
        <link rel="canonical" href={seo.url} />
        <meta key="meta-desc" name="description" content={seo.description} />
        <meta key="meta-image" name="image" content={seo.image} />
        {getAlternates(path)}
      </Head>
      <Facebook
        desc={seo.description}
        image={seo.image}
        title={seo.title}
        type="website"
        url={seo.url}
        locale={ogLanguage}
      />
      <Twitter title={seo.title} image={seo.image} desc={seo.description} />
    </>
  );
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
});

export default SEO;
